<script lang="ts">
  interface Props {
    variant?: "primary" | "secondary";
    size?: "sm" | "md" | "lg";
    href?: string;
    onclick?: () => void;
    disabled?: boolean;
    icon?: string;
    iconPosition?: "left" | "right";
    fullWidth?: boolean;
    children?: import("svelte").Snippet;
    className?: string;
  }

  let {
    variant = "primary",
    size = "md",
    href = undefined,
    onclick = undefined,
    disabled = false,
    icon = undefined,
    iconPosition = "left",
    fullWidth = false,
    className = "",
    children,
  }: Props = $props();

  // Size classes
  const sizeClasses = {
    sm: "h-10 py-2 text-sm",
    md: "h-12 py-3 text-base",
    lg: "h-[50px] py-3 text-lg",
  };

  // Variant classes
  const variantClasses = {
    primary:
      "bg-[#FAFAF9] text-black border-transparent hover:bg-[#F5F5F4] active:bg-[#F0F0EF] shadow-[inset_0px_-0.5px_0px_0px_rgba(0,0,0,0.08),inset_0px_2px_3px_0px_#FFF,inset_0px_0px_16px_0px_rgba(41,37,36,0.3)]",
    secondary: "text-white border-opacity-0 border-neutral-600 hover:bg-neutral-900 active:bg-neutral-700",
  };

  const buttonClasses = $derived(
    `
  inline-flex items-center justify-center gap-2 rounded-full border font-medium
  transition-all duration-200 ease-in-out
  disabled:opacity-50 disabled:cursor-not-allowed
  pl-4 pr-2
  ${sizeClasses[size]}
  ${variantClasses[variant]}
  ${fullWidth ? "w-full" : "w-fit"}
  ${className}
`.trim(),
  );

  // Size-responsive styling
  const iconSizeClasses = {
    sm: "w-4 h-4",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  const sendWrappedIconSizeClasses = {
    sm: "w-2 h-2",
    md: "w-2.5 h-2.5",
    lg: "w-3 h-3",
  };

  const sendWrappedPaddingClasses = {
    sm: "p-1.5",
    md: "p-2",
    lg: "p-2.5",
  };

  const sendWrappedMarginClasses = {
    sm: "ml-1",
    md: "ml-1.5",
    lg: "ml-2",
  };

  // Determine icon wrapper background and icon color based on variant
  const iconWrapperBg = $derived(variant === "primary" ? "bg-black" : "bg-white");
  const iconColor = $derived(variant === "primary" ? "text-white" : "text-black");

  function renderIconContent(iconType: string) {
    switch (iconType) {
      case "send-wrapped":
        return `
        <path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" fill="currentColor"/>
      `;
      case "play":
        return '<polygon points="5,3 19,12 5,21"></polygon>';
      case "arrow-right":
        return `
        <line x1="5" y1="12" x2="19" y2="12"></line>
        <polyline points="12,5 19,12 12,19"></polyline>
      `;
      default:
        return "";
    }
  }

  // Create the icon element as a string to avoid repetition
  const iconElement = $derived(
    icon
      ? icon === "send-wrapped"
        ? `<div class="${iconWrapperBg} rounded-full ${sendWrappedMarginClasses[size]} ${sendWrappedPaddingClasses[size]} flex items-center justify-center shadow-sm">
        <svg class="${sendWrappedIconSizeClasses[size]} ${iconColor}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          ${renderIconContent(icon)}
        </svg>
       </div>`
        : `<svg class="${iconSizeClasses[size]}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          ${renderIconContent(icon)}
       </svg>`
      : "",
  );
</script>

{#if href}
  <a {href} class={buttonClasses} class:pointer-events-none={disabled} {onclick}>
    {#if icon && iconPosition === "left"}
      {@html iconElement}
    {/if}
    {@render children?.()}
    {#if icon && iconPosition === "right"}
      {@html iconElement}
    {/if}
  </a>
{:else}
  <button type="button" class={buttonClasses} {disabled} {onclick}>
    {#if icon && iconPosition === "left"}
      {@html iconElement}
    {/if}
    {@render children?.()}
    {#if icon && iconPosition === "right"}
      {@html iconElement}
    {/if}
  </button>
{/if}
